// Type definitions for the mode-level urgency indicator feature (D1).
//
// Doctrine: see `docs/planning/style_doctrine.md` §"Mode-level urgency
// indicators (NEW — from D1)" and the matching workstream #6 entry.
//
// Surface contract:
//   - A `ModeUrgencyResolver` is a pure function over a shared
//     `ModeUrgencyContext` data bag (leads + ads activity + auth + nowMs).
//   - The optional `MODE_URGENCY_RESOLVERS` map in `modeUrgencyResolvers.ts`
//     opts a mode in by registering one resolver per `PrimaryMode` key.
//   - Resolvers MUST return `{ active: false }` when no urgency is present;
//     never throw. The chrome surfaces (`SidebarModeList`,
//     `MobileBottomTabBar`) treat `active: false` as "render nothing."

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { KpiTone } from "@/components/common/kpi";
import type { AdsActivityResponse } from "@/lib/adsAssistantClientEngagement";
import type { Lead } from "@/types/lead";

/**
 * Result of asking "does this mode need attention right now?"
 *
 * - `active: false` → no badge rendered.
 * - `active: true`  → badge renders with the chosen KPI tone. `reason` powers
 *   the `title` tooltip; `ariaLabel` powers the `aria-label` on the mode
 *   button so color is never the sole signal.
 */
export type ModeUrgency =
  | { active: false }
  | { active: true; tone: KpiTone; reason: string; ariaLabel: string };

/**
 * Read-only data bag passed to every resolver. The shell-level
 * `WorkspaceUrgencyProvider` populates this once per polling tick; resolvers
 * are pure-functional over it.
 *
 * `auth` is nullable to match `useAuthOptional()` — resolvers must treat the
 * pre-bootstrap case as "no urgency."
 */
export type ModeUrgencyContext = {
  leads: Lead[];
  adsActivity: AdsActivityResponse | null;
  auth: AuthContextValue | null;
  nowMs: number;
};

export type ModeUrgencyResolver = (ctx: ModeUrgencyContext) => ModeUrgency;

/** Sentinel used by `useModeUrgency` and resolvers for the no-urgency case. */
export const NO_URGENCY: ModeUrgency = { active: false };
